import './card.css'

import React, { FC, PropsWithChildren, useEffect, useImperativeHandle, useRef, useState } from "react";

/**
 * Card component with automatic click propagation prevention.
 *
 * The Card component automatically prevents click propagation for:
 * - Standard HTML buttons, inputs, selects, textareas, and links
 * - Elements with role="button"
 * - Elements with CSS classes containing 'btn' or 'button'
 * - Elements with click handlers or tabindex attributes
 * - Kendo UI components (k-button, k-textbox, etc.)
 *
 * For custom interactive elements that need to prevent card clicks,
 * you can also manually call preventCardClick(event) in your onClick handler:
 *
 * Example:
 * <div onClick={(e) => { preventCardClick(e); handleCustomClick(); }}>
 *   Custom clickable content
 * </div>
 */
import { isArrayEmpty, isNullOrEmpty, isObjectNull, stopPropagation, preventCardClick } from '../../helpers/ObjectHelpers';
import { Dialog } from '../dialogs/Dialog';
import { DialogProperties } from '../dialogs/DialogProperties';
import { CardProps } from './props/CardProps';
import { CardLeadingActions } from './components/CardLeadingActions';
import { CardTrailingActions } from './components/CardTrailingActions';
import { CardContent } from './components/CardContent';
import { ICardService } from './services/ICardService';
import CardService from './services/CardService';
import { ICardDispatcherService } from './services/ICardDispatcherService';
import CardDispatcherService from './services/CardDispatcherService';
import Slide from '../Slide';
import { CardContentProps } from './props/CardContentProps';

export const Card: FC<PropsWithChildren<CardProps>> = ({
    ref,
    children,
    id = 0,
    title = "",
    subTitle = "",
    subTitleClassName = "",
    borderColor = "var(--card-border)",
    backgroundColor = "var(--card-body-bg)",
    indicatorPrimaryColor = "var(--card-border)",
    indicatorSecondaryColor = "",
    actions = [],
    actionsDisplay = "dropdown",
    showIndicator = false,
    confirmDelete = false,
    canAccept = false,
    canSelect = false,
    canDelete = false,
    confirmDeleteTitle = null,
    confirmDeleteText = null,
    deleted = false,
    added = false,
    swipeMode = "default",
    showDeleteButtonOnMobile = false,
    truncateSubTitle = true,
    icons = [],
    iconSize = 16,
    onAccept = null,
    onDelete = null,
    onClick = null,
    onToggleSelect = null,
    onSelect = null,
    onActivateSelect = null,
    onDeactivateSelect = null,
    isDirty = false,
    clipboardText = "",
    systemName = "",
    showSelect = false,
    pinSelect = false,
    selected = false,
    allowBothSelectAndClick = false,
}) => {
    useImperativeHandle(ref, () => ({
        onToggleSelect(show: boolean) {
            if (!canSelect) return;

            cardService.onToggleSelect(show);

            if (!show)
                setIsSelected(false);
        },
        onSelect(select: boolean) {
            if (!canSelect) return;
            setIsSelected(select);
        },
        onDeleted(deletedId: number) {
            if (id !== deletedId) return;
            setIsDeleted(true);

            setTimeout(() => {
                setShouldRemove(true);
            }, 750);

            if (!onSelect) return;
            onSelect(id, false);
        },
        onSuccess() {
            contentRef.current?.onSuccess();
        },
        onError() {
            contentRef.current?.onError();
        },
        onReset(refId: number) {
            if (id !== refId) return;
            cardService.onReset();
        }
    }));

    const hasOnToggleSelect = !isObjectNull(onToggleSelect);
    const hasOnClick = !isObjectNull(onClick);

    const [isSelected, setIsSelected] = useState(selected);
    const [showDialog, setShowDialog] = useState(false);
    const [isDeleted, setIsDeleted] = useState(deleted);
    const [shouldRemove, setShouldRemove] = useState(deleted);
    const [show, setShow] = useState(!added);

    const hasActions = !isArrayEmpty(actions);

    const containerRef = useRef<HTMLDivElement>(null);
    const cardServiceRef = useRef<ICardService | null>(null);
    const dispatcherServiceRef = useRef<ICardDispatcherService | null>(null);
    const contentRef = useRef<CardContentProps>(null);

    if (!cardServiceRef.current) {
        cardServiceRef.current = new CardService(
            id,
            swipeMode,
            showIndicator,
            canAccept,
            canSelect,
            canDelete,
            hasActions,
            !isNullOrEmpty(title),
            !isNullOrEmpty(subTitle),
            hasOnToggleSelect,
            hasOnClick,
            pinSelect
        );
        if (showSelect) {

            setTimeout(() => {
                cardServiceRef?.current?.onToggleSelect(showSelect?? false);
            }, 125);
        }

    }

    if (cardServiceRef.current !== null && cardServiceRef.current.setCanDelete) {
        cardServiceRef.current.setCanDelete(canDelete);
    }

    if (cardServiceRef.current.hasActions !== hasActions) {
        cardServiceRef.current.setHasActions(hasActions);
    }

    if (!dispatcherServiceRef.current) {
        dispatcherServiceRef.current = new CardDispatcherService(
            id,
            setIsSelected,
            onClick,
            onAccept,
            onSelect,
            onToggleSelect,
            onActivateSelect,
            onDeactivateSelect,
            onDelete,
            confirmDelete,
            setShowDialog,
            systemName,
            allowBothSelectAndClick
        );
        if (cardServiceRef.current) {
            const dispatcher = dispatcherServiceRef.current;
            cardServiceRef.current.dispatcherService = dispatcher;
        }
    }

    if (dispatcherServiceRef.current && dispatcherServiceRef.current.setOnDelete) {
        dispatcherServiceRef.current.setOnDelete(onDelete);
    }

    const cardService = cardServiceRef.current;
    const dispatcherService = dispatcherServiceRef.current;

    const setBaseValues = () => {
        const elem = containerRef.current;
        if (!elem) return;
        cardService.setContainerWidth(elem.getBoundingClientRect().width);
    }

    useEffect(() => {
        setBaseValues();
    }, [containerRef]);

    useEffect(() => {
        if (added) {
            setTimeout(() => {
                setShow(true);
            }, 250);
        }

        const element = containerRef.current;
        if (!element) return;

        const resizeObserver = new ResizeObserver(() => {
            setBaseValues();
        });

        resizeObserver.observe(element);

        return (() => {
            cardServiceRef.current = null; // Clear the reference
            dispatcherServiceRef.current = null; // Clear the reference
            resizeObserver.disconnect();
        })
    }, []);

    useEffect(() => {
        dispatcherService.onSelect(isSelected);
    }, [isSelected]);

    useEffect(() => {
        setShow(!isDeleted);
    }, [isDeleted]);

    useEffect(() => {
        if (cardServiceRef.current && cardServiceRef.current.setCanAccept) {
            cardServiceRef.current.setCanAccept(canAccept);
        }
    }, [canAccept]);

    return (
        <>
            <CardContainer
                canShow={canDelete}
                show={show}>
                <article
                    ref={containerRef}
                    id={`card-item-${id}`}
                    className={`crd-container ${cardService.classNameContainer} ${isDeleted ? "crd-container-deleted" : ""}`}
                    style={{ display: shouldRemove ? "none" : "grid" }}>
                    <CardLeadingActions
                        cardService={cardService}
                        canAccept={canAccept}
                        canSelect={canSelect}
                        isSelected={isSelected}
                        dispatcherService={dispatcherService}
                        allowBothSelectAndClick={allowBothSelectAndClick}
                    />
                    <CardContent
                        ref={contentRef}
                        id={id}
                        cardService={cardService}
                        borderColor={borderColor}
                        backgroundColor={backgroundColor}
                        indicatorPrimaryColor={indicatorPrimaryColor}
                        indicatorSecondaryColor={indicatorSecondaryColor}
                        title={title}
                        subTitle={subTitle}
                        subTitleClassName={subTitleClassName}
                        icons={icons}
                        iconSize={iconSize}
                        actions={actions}
                        actionsDisplay={actionsDisplay}
                        dispatcherService={dispatcherService}
                        onItemClick={onItemClick}
                        isDirty={isDirty}
                        showDeleteButtonOnMobile={showDeleteButtonOnMobile}
                        truncateSubTitle={truncateSubTitle}
                        clipboardText={clipboardText}
                        onClick={onClick}>
                            {children}
                    </CardContent>
                    <CardTrailingActions
                        cardService={cardService}
                        dispatcherService={dispatcherService}
                    />
                </article>
            </CardContainer>
            {
                showDialog &&
                <Dialog
                    properties={{
                        ...DialogProperties,
                        mode: "confirmation",
                        title: isNullOrEmpty(confirmDeleteTitle) ? "Slett" : confirmDeleteTitle,
                        onClose: onCloseDialog,
                        onClick: () => dispatcherService.onThenDelete() ,
                        text: isNullOrEmpty(confirmDeleteText) ? "&Oslash;nsker du &aring; slette valgte element?" : confirmDeleteText
                    }}
                />
            }
        </>
    )

    function onItemClick(e: any) {
        stopPropagation(e);
        e.item.onClick();
    }

    function onCloseDialog() {
        cardService.onCloseDialog();
        setShowDialog(false);
    }
}

interface CardContainerProps {
    canShow?: boolean,
    show?: boolean
}
const CardContainer = ({
    children,
    canShow = false,
    show = false
}: PropsWithChildren<CardContainerProps>) => {
    return (
        canShow ?
            <Slide show={show}>
                {children}
            </Slide>
            :
        children
    )
}
